// Owner Request Form — multi-step anonymous property request creation
// UX: Quick-select chips for timeline, guided notes, privacy reinforcement
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, MapPin, Home, Clock, MessageSquare, FileText } from "lucide-react";
import { Button, Card, Alert } from "@/components/shared";
import { api } from "@/services/api";
import {
  Province,
  PropertyType,
  Timeline,
  RequestType,
  ContactPreference,
  ProvinceLabels,
  PropertyTypeLabels,
  TimelineLabels,
  type CreateOwnerRequestPayload,
} from "@/types";

type Step = 1 | 2 | 3;

const SUGGESTION_CHIPS = [
  "Accurate valuation",
  "Low-pressure approach",
  "Fast sale",
  "Rental management",
  "Area specialist",
  "Strong marketing",
  "Commission guidance",
];

export function OwnerRequestForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateOwnerRequestPayload>({
    suburb: "",
    province: Province.Gauteng,
    property_type: PropertyType.Freehold,
    price_band_min: 1000000,
    price_band_max: 2000000,
    timeline: Timeline.ThreeToSix,
    request_type: RequestType.Sale,
    contact_preference: ContactPreference.PlatformOnly,
    seller_notes: "",
  });

  function updateField<K extends keyof CreateOwnerRequestPayload>(field: K, value: CreateOwnerRequestPayload[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function addChip(chip: string) {
    const current = formData.seller_notes || "";
    const notes = current ? `${current}, ${chip}` : chip;
    updateField("seller_notes", notes);
  }

  async function handleSubmit() {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post("/owner-requests", formData);
      navigate("/owner");
    } catch (e: any) {
      setError(e.message || "Failed to create request");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Privacy Banner */}
      <div className="mb-6 flex items-center gap-3 p-4 bg-gold/10 rounded-lg border border-gold/30">
        <LockKeyhole className="h-5 w-5 text-sun flex-shrink-0" />
        <p className="text-sm text-sun">
          Your name, number, and exact address remain hidden until you approve agent access.
        </p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white/70">Step {step} of 3</span>
          <span className="text-muted">
            {step === 1 ? "Property Details" : step === 2 ? "Your Timeline" : "Agent Communication"}
          </span>
        </div>
        <div className="h-2 bg-white/10 rounded-full">
          <div
            className="h-2 bg-sun rounded-full transition-all"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {/* Step 1: Property Details */}
      {step === 1 && (
        <Card>
          <h2 className="heading-section mb-1 flex items-center gap-2">
            <Home className="h-5 w-5 text-sun" />
            Tell agents about your property
          </h2>
          <p className="text-muted mb-6">Your exact address will not be shared.</p>

          <div className="space-y-5">
            {/* Request Type */}
            <div>
              <label className="label">I want to...</label>
              <div className="flex gap-3">
                {Object.values(RequestType).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateField("request_type", type)}
                    className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${
                      formData.request_type === type
                        ? "border-gold bg-gold/10 text-sun"
                        : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {type === RequestType.Sale ? "Sell my property" : "Rent my property"}
                  </button>
                ))}
              </div>
            </div>

            {/* Suburb */}
            <div>
              <label className="label">Suburb</label>
              <p className="text-xs text-white/40 mb-2">Your exact address will not be shared.</p>
              <input
                type="text"
                value={formData.suburb}
                onChange={(e) => updateField("suburb", e.target.value)}
                placeholder="e.g. Fourways, Sandton, Sea Point"
                className="input"
              />
            </div>

            {/* Province */}
            <div>
              <label className="label">Province</label>
              <div className="flex gap-3 flex-wrap">
                {Object.values(Province).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => updateField("province", p)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      formData.province === p
                        ? "border-gold bg-gold/10 text-sun"
                        : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {ProvinceLabels[p]}
                  </button>
                ))}
              </div>
            </div>

            {/* Property Type */}
            <div>
              <label className="label">Property type</label>
              <div className="flex gap-2 flex-wrap">
                {Object.values(PropertyType).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateField("property_type", type)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      formData.property_type === type
                        ? "border-gold bg-gold/10 text-sun"
                        : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {PropertyTypeLabels[type]}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Band */}
            <div>
              <label className="label">Estimated property value</label>
              <p className="text-xs text-white/40 mb-2">The agent you choose will provide a free market evaluation.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="number"
                    value={formData.price_band_min}
                    onChange={(e) => updateField("price_band_min", Number(e.target.value))}
                    min={100000}
                    step={100000}
                    className="input"
                    placeholder="Min (R)"
                  />
                  <span className="text-xs text-white/40">Min: R{(formData.price_band_min / 1000000).toFixed(1)}M</span>
                </div>
                <div>
                  <input
                    type="number"
                    value={formData.price_band_max}
                    onChange={(e) => updateField("price_band_max", Number(e.target.value))}
                    min={formData.price_band_min}
                    step={100000}
                    className="input"
                    placeholder="Max (R)"
                  />
                  <span className="text-xs text-white/40">Max: R{(formData.price_band_max / 1000000).toFixed(1)}M</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!formData.suburb}>
              Continue
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Timeline */}
      {step === 2 && (
        <Card>
          <h2 className="heading-section mb-1 flex items-center gap-2">
            <Clock className="h-5 w-5 text-sun" />
            When are you planning to move?
          </h2>
          <p className="text-muted mb-6">This helps agents prioritize their response.</p>

          <div className="grid grid-cols-2 gap-3">
            {Object.values(Timeline).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => updateField("timeline", t)}
                className={`p-4 rounded-xl border text-left transition-colors ${
                  formData.timeline === t
                    ? "border-gold bg-gold/10 ring-2 ring-gold/20"
                    : "border-white/15 hover:border-white/30"
                }`}
              >
                <span className="text-sm font-medium text-white">{TimelineLabels[t]}</span>
              </button>
            ))}
          </div>

          <div className="mt-6 flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={() => setStep(3)}>Continue</Button>
          </div>
        </Card>
      )}

      {/* Step 3: Communication & Notes */}
      {step === 3 && (
        <Card>
          <h2 className="heading-section mb-1 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-sun" />
            How may agents contact you?
          </h2>
          <p className="text-muted mb-6">Choose your preferred communication method.</p>

          <div className="space-y-3 mb-6">
            {[
              { value: ContactPreference.PlatformOnly, label: "Messages only through the platform", desc: "Safest option — no direct calls or WhatsApps" },
              { value: ContactPreference.AfterApproval, label: "Contact me only after approval", desc: "Agents can reach out after you unlock" },
              { value: ContactPreference.NoDirectContact, label: "No direct calls or WhatsApps", desc: "All communication stays in-platform" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => updateField("contact_preference", option.value)}
                className={`w-full p-4 rounded-xl border text-left transition-colors ${
                  formData.contact_preference === option.value
                    ? "border-gold bg-gold/10 ring-2 ring-gold/20"
                    : "border-white/15 hover:border-white/30"
                }`}
              >
                <span className="text-sm font-medium text-white">{option.label}</span>
                <p className="text-xs text-white/40 mt-0.5">{option.desc}</p>
              </button>
            ))}
          </div>

          {/* Guided notes */}
          <div>
            <label className="label flex items-center gap-2">
              <FileText className="h-4 w-4" />
              What are you looking for from an agent?
            </label>
            <div className="flex gap-2 flex-wrap mb-3">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => addChip(chip)}
                  className="px-3 py-1 text-xs rounded-full border border-white/20 text-white/60 hover:border-gold/40 hover:text-sun transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
            <textarea
              value={formData.seller_notes || ""}
              onChange={(e) => updateField("seller_notes", e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="e.g. Looking for an agent with strong buyer database in my suburb..."
              className="input resize-none"
            />
            <span className="text-xs text-white/40">{(formData.seller_notes || "").length}/1000</span>
          </div>

          <div className="mt-6 flex justify-between">
            <Button variant="secondary" onClick={() => setStep(2)}>Back</Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              Create Anonymous Request
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
